import { useRef } from 'react';
import { toast, dismiss } from '../toast/toastStore';
import { useServiceWorker } from '../hooks/useServiceWorker';

/**
 * Service Worker の更新/オンライン状態を v2 トーストで通知する。
 * 旧 antd notification を v2 トースト(アクションボタン対応)に置換。
 */
const ServiceWorkerUpdateNotification: React.FC = () => {
  // オフライン通知の toast id を保持し、オンライン復帰時に閉じる
  const offlineToastIdRef = useRef<string | null>(null);
  // 更新通知の toast id を保持し、再発火時に重複を防ぐ(旧 antd key dedupe 相当)
  const updateToastIdRef = useRef<string | null>(null);

  const { skipWaiting } = useServiceWorker({
    onUpdate: () => {
      showUpdateNotification();
    },
    onOffline: () => {
      showOfflineNotification();
    },
    onOnline: () => {
      if (offlineToastIdRef.current) {
        dismiss(offlineToastIdRef.current);
        offlineToastIdRef.current = null;
      }
      showOnlineNotification();
    },
  });

  const showUpdateNotification = () => {
    // 既存の更新トーストがあれば閉じてから出す(重複スタック防止)
    if (updateToastIdRef.current) {
      dismiss(updateToastIdRef.current);
    }
    updateToastIdRef.current = toast.notify({
      type: 'info',
      message: '新しいバージョンが利用可能です',
      description:
        'アプリケーションの新しいバージョンがダウンロードされました。更新するにはページを再読み込みしてください。',
      durationSec: 0, // 自動で閉じない
      action: {
        label: '今すぐ更新',
        onClick: () => {
          skipWaiting();
          // 少し遅延を入れてからリロード
          setTimeout(() => {
            window.location.reload();
          }, 100);
        },
      },
    });
  };

  const showOfflineNotification = () => {
    offlineToastIdRef.current = toast.notify({
      type: 'warning',
      message: 'オフラインモード',
      description:
        'インターネット接続が切断されました。一部の機能が制限される可能性があります。',
      durationSec: 0,
    });
  };

  const showOnlineNotification = () => {
    toast.notify({
      type: 'success',
      message: 'オンラインに復帰しました',
      description: 'インターネット接続が回復しました。全ての機能が利用可能です。',
      durationSec: 3,
    });
  };

  // このコンポーネントは何も描画しない（通知のみ）
  return null;
};

export default ServiceWorkerUpdateNotification;
